import type React from "react";
import { memo, useRef } from "react";

import { MarketRowCell } from "./MarketRowCell";
import { MarketRowDynamicCells } from "./MarketRowDynamicCells";
import { Row } from "./styles";

interface MarketRowProps {
  marketAddr: string;
  baseSymbol: string;
  symbol: string;
  tokenIcon?: string;
  maxLeverage: string | number;
  isFavorite: boolean;
  onSelect: (marketAddr: string) => void;
  onToggleFavorite: (address: string) => void;
}

// Memo - Row and static cell don't re-render; only MarketRowDynamicCells (observer) re-renders on ticker updates
export const MarketRow = memo(
  ({
    marketAddr,
    baseSymbol,
    symbol,
    tokenIcon,
    maxLeverage,
    isFavorite,
    onSelect,
    onToggleFavorite,
  }: MarketRowProps) => {
    const touchStartPos = useRef<{ x: number; y: number } | null>(null);

    const handleRowClick = () => {
      onSelect(marketAddr);
    };

    const handleTouchStart = (e: React.TouchEvent) => {
      const touch = e.touches[0];
      touchStartPos.current = { x: touch.clientX, y: touch.clientY };
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
      if (!touchStartPos.current) return;

      const touch = e.changedTouches[0];
      const deltaX = Math.abs(touch.clientX - touchStartPos.current.x);
      const deltaY = Math.abs(touch.clientY - touchStartPos.current.y);

      if (deltaX < 10 && deltaY < 10) {
        e.preventDefault();
        onSelect(marketAddr);
      }
      touchStartPos.current = null;
    };

    return (
      <Row
        data-testid={`market-select:row:${baseSymbol}`}
        onClick={handleRowClick}
        onTouchEnd={handleTouchEnd}
        onTouchStart={handleTouchStart}
      >
        <MarketRowCell
          baseSymbol={baseSymbol}
          symbol={symbol}
          tokenIcon={tokenIcon}
          maxLeverage={maxLeverage}
          isFavorite={isFavorite}
          marketAddr={marketAddr}
          onToggleFavorite={onToggleFavorite}
        />
        <MarketRowDynamicCells marketAddr={marketAddr} baseSymbol={baseSymbol} />
      </Row>
    );
  },
);

MarketRow.displayName = "MarketRow";
